import React from 'react'

import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'

import type { LineSeriesType, HighlightItemData, LineItemIdentifier } from '@mui/x-charts'
import { LineChart } from '@mui/x-charts/LineChart'

import { useEvent, ANALYTICS_EVENTS } from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { DebtChartData } from '../../core/utils/DebtUtils'
import { adjustBrightness, BASE_LINE_COLOR } from '../utils/DebtsChart'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface DebtsChartProps {
  sx?: any
  height?: number
  width?: number
  debts: DebtChartData[]
  hoveredDebtGuid?: string
  onClickArea?: () => void
  onHoverArea?: (debtGuid: string) => void
}

interface DebtsChartSeries extends LineSeriesType {
  guid?: string | null
}

export const DebtsChart: React.FC<DebtsChartProps> = ({
  sx,
  height = 500,
  width,
  debts,
  hoveredDebtGuid,
  onClickArea,
  onHoverArea,
}) => {
  const theme = useTheme()
  const { onEvent } = useEvent()

  const possibleDebts = debts.filter((d) => !d.is_impossible)

  // Generate sorted array of unique dates
  const uniqueXValues = [
    ...new Set(
      possibleDebts.flatMap((debt) => debt.dataset.map((point) => new Date(point.x).getTime())),
    ),
  ]
    .sort((a, b) => a - b)
    .map((timestamp) => new Date(timestamp))

  // Normalize datasets with missing dates
  const normalizedDatasets = possibleDebts.map((debt) => {
    const datasetMap = new Map(debt.dataset.map((point) => [new Date(point.x).getTime(), point.y]))

    let lastY = datasetMap.get(uniqueXValues[0].getTime()) ?? 0
    const normalizedDataset = uniqueXValues.map((x) => {
      const y = datasetMap.get(x.getTime()) ?? lastY // Use last known value for missing points
      lastY = y
      return { x, y }
    })

    return { ...debt, dataset: normalizedDataset }
  })

  const initialTotalDebt = normalizedDatasets.reduce(
    (sum, debt) => sum + (debt.dataset[0]?.y ?? 0),
    0,
  )

  const payoffLine = uniqueXValues.map((x, index) => {
    const linearRate = index / (uniqueXValues.length - 1)
    const y = initialTotalDebt * (1 - linearRate)
    return { x, y }
  })

  const yMax = Math.ceil(initialTotalDebt / 100) * 100
  const xData = uniqueXValues

  const series: DebtsChartSeries[] = normalizedDatasets.map((debt, index) => {
    const brightnessFactor = 1 - (index / normalizedDatasets.length) * 0.5
    const seriesColor = adjustBrightness(BASE_LINE_COLOR, brightnessFactor)

    return {
      id: `debt-${index}`,
      guid: debt.guid,
      label: debt.name,
      data: debt.dataset.map((point) => point.y),
      color: seriesColor,
      curve: 'linear',
      type: 'line',
      area: true,
      stack: 'total',
      showMark: false,
    }
  })

  series.push({
    id: 'payoff-line',
    label: 'Payoff Line',
    data: payoffLine.map((point) => point.y),
    color: theme.palette.grey[700],
    curve: 'linear',
    type: 'line',
    stack: undefined,
    showMark: false,
    disableHighlight: true,
  })

  const getIndexByGuid = (guid: string) => {
    return series.findIndex((s) => s?.guid === guid)
  }

  const onHighlightChange = (line: HighlightItemData | null) => {
    const highlightedGuid = series.find((s) => s.id === line?.seriesId)?.guid
    onHoverArea?.(highlightedGuid ?? '')
  }

  const onAreaClick = (
    _event: React.MouseEvent<SVGElement, MouseEvent>,
    lineItemIdentifier: LineItemIdentifier,
  ) => {
    onClickArea?.()
    const areaSeries = series.find((s) => s.id === lineItemIdentifier.seriesId)
    onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_CHART_AREA, { account_guid: areaSeries?.guid })
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        touchAction: 'pan-y',
        '& svg': {
          touchAction: 'pan-y',
          userSelect: 'none',
        },
        '& .recharts-wrapper': {
          touchAction: 'pan-y',
        },
      }}
    >
      <LineChart
        axisHighlight={{ x: 'none' }}
        disableLineItemHighlight={true}
        height={height}
        hideLegend={true}
        margin={{ bottom: 24, left: 32, right: 72, top: 48 }}
        onAreaClick={onAreaClick}
        onHighlightChange={onHighlightChange}
        series={series}
        skipAnimation={true}
        slotProps={{ tooltip: { trigger: 'none' } }}
        sx={{
          p: 4,
          '& .MuiBarLabel-root': {
            fontSize: 10,
            fontWeight: 600,
          },
          '& .MuiAreaElement-root': {
            opacity: 0.4,
            transition: 'opacity 0.3s ease',
          },
          '& .MuiAreaElement-root:hover': {
            opacity: 1,
          },
          '& .MuiLineElement-series-payoff-line': {
            strokeDasharray: '10 5',
            strokeWidth: 1,
          },
          [`& .MuiLineElement-series-debt-${getIndexByGuid(hoveredDebtGuid ?? '0')}`]: {
            opacity: 1,
          },
          [`& .MuiAreaElement-series-debt-${getIndexByGuid(hoveredDebtGuid ?? '0')}`]: {
            opacity: 1,
          },
          '& .MuiChartsAxis-tickLabel > tspan': {
            fontFamily: theme.typography.body2.fontFamily,
          },
          ...sx,
        }}
        width={width}
        xAxis={[
          {
            data: xData,
            disableTicks: true,
            min: uniqueXValues[0],
            scaleType: 'time',
            tickLabelMinGap: 24,
            valueFormatter: (value: Date) => formatDate(value, DATE_FORMATS_INTL.MONTH_SHORT_YEAR),
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: yMax,
            disableTicks: true,
            valueFormatter: (value: number) => formatCurrency(value, '0a'),
          },
        ]}
      />
    </Box>
  )
}
